import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import { InfoField } from '@components-clean/core/info-field';
import { TextAreaController } from '@components-clean/core/text-area';
import style from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/dialogs/style.module.scss';
import { useTranslation } from '@hooks/index';
import { SubjectReviewInstanceApi } from '@apis/bff-api/subject-monitoring/subject-review-instance';

type SubjectRow = {
    id: number;
    code: string;
};

type RemoveFromReviewBulkDialogProps = {
    datasetId: number;
    studyRef: string;
    subjects: SubjectRow[];
    removeAll: boolean;
    reviewRecordId: number;
    sessionId: string;
    totalRows?: number;
    onClose: () => void;
    onSuccess: () => void;
    onError: () => void;
};

interface IRemoveFromReviewBulkForm {
    reason: string;
}

const RemoveFromReviewBulkDialog = ({
    datasetId,
    studyRef,
    subjects,
    removeAll,
    reviewRecordId,
    sessionId,
    totalRows,
    onClose,
    onSuccess,
    onError,
}: RemoveFromReviewBulkDialogProps) => {
    const { t } = useTranslation('sm', { keyPrefix: 'subjectHub.removeFromReviewBulkDialog' });
    const { t: tCommon } = useTranslation(['common']);

    const { trigger: removeFromReview, isMutating: isSaving } = SubjectReviewInstanceApi.useRemoveFromReview(studyRef);

    const [showConfirmation, setShowConfirmation] = useState(false);

    const subjectCount = removeAll ? (totalRows ?? 0) : subjects.length;
    const subjectsPlural = subjectCount === 1 ? t('subject') : t('subjects');

    const {
        control,
        handleSubmit,
        formState: { isDirty, isValid, isSubmitting },
    } = useForm<IRemoveFromReviewBulkForm>({
        mode: 'all',
        defaultValues: { reason: '' },
    });

    const onSubmitHandler = () =>
        handleSubmit(async (formData) => {
            if (removeAll && !showConfirmation) {
                setShowConfirmation(true);
                return;
            }

            const success = await removeFromReview({
                datasetId,
                reviewRecordId,
                reason: formData.reason,
                // If we're removing all, don't need to send the subject codes, just the flag
                subjectIds: removeAll ? [] : subjects.map((subject) => subject.id),
                removeAll,
                sessionId,
            });

            if (!success) {
                onError();
            } else {
                onSuccess();
            }
        })();

    const title = showConfirmation ? t('confirmTitle') : removeAll ? t('titleRemoveAll') : t('titleRemoveSelected');

    return (
        <DraggableDialog
            dataTestId="remove-from-review-bulk-dialog"
            onClose={onClose}
            onSubmit={onSubmitHandler}
            title={title}
            primaryBtnDisabled={!isDirty || !isValid || isSubmitting || isSaving}
            primaryBtnLabel={showConfirmation ? tCommon('continue') : t('confirmButton')}
            secondaryBtnDisabled={isSubmitting || isSaving}
            height={265}
            initialWidth={530}
            bodyPadding="none"
        >
            {showConfirmation ? (
                <div className="k-d-flex k-align-items-center k-h-full" style={{ padding: '8px 11px' }}>
                    <span>{t('confirmMessage', { subjectCount })}</span>
                </div>
            ) : (
                <form className="k-h-full k-w-full k-d-flex k-flex-column k-gap-2" style={{ padding: '8px 11px' }}>
                    <div className="k-d-grid k-pb-2">
                        <InfoField
                            message={
                                removeAll ? t('removeAllWarning') : t('infoMessage', { subjectCount, subjectsPlural })
                            }
                            layout="stretch"
                            type={removeAll ? 'Warning' : 'Info'}
                        />
                    </div>
                    <TextAreaController
                        control={control}
                        name="reason"
                        data-testid="remove-from-review-bulk-reason"
                        label={t('reasonLabel')}
                        required
                        maxLength={3000}
                        className={style.textAreaOverrides}
                        readOnly={isSubmitting}
                    />
                </form>
            )}
        </DraggableDialog>
    );
};

export default RemoveFromReviewBulkDialog;
